import YearlyCycleLine from "@dashboard/components/Line/YearlyCycleLine";
import type { AnnualCycleFilters } from "@dashboard/hooks/useAnnualCycleTable";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import AnnualCycleTable from "./AnnualCycleTable";

export function AnnualCyclePage() {
    const navigate = useNavigate();
    const search = useSearch({ from: "/_hub/$customerId/dashboard/$profileId/annual-cycle" });

    const today = useMemo(() => new Date(), []);
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    const filters = useMemo<AnnualCycleFilters>(
        () => ({
            search: search.search ?? "",
            selectedMonths: search.selectedMonths ?? [currentMonth],
            status: search.status ?? [],
            responsible: search.responsible ?? [],
            year: search.year ?? currentYear,
        }),
        [search, currentMonth, currentYear],
    );

    const setFilter = <K extends keyof AnnualCycleFilters>(
        key: K,
        value: AnnualCycleFilters[K],
    ) => {
        void navigate({
            to: ".",
            search: (prev) => ({ ...prev, [key]: value }),
        });
    };

    const handleMonthClick = (month: number) => {
        const current = filters.selectedMonths;
        const next = current.includes(month)
            ? current.filter((m) => m !== month)
            : [...current, month];
        setFilter("selectedMonths", next);
    };

    const handleYearChange = (newYear: number) => {
        void navigate({
            to: ".",
            search: (prev) => ({ ...prev, year: newYear }),
        });
    };

    const handleClearFilters = () => {
        void navigate({
            to: ".",
            search: (prev) => ({
                ...prev,
                search: undefined,
                selectedMonths: [],
                status: undefined,
                responsible: undefined,
            }),
        });
    };

    const hasFilter = <K extends keyof AnnualCycleFilters>(key: K): boolean => {
        const value = filters[key];
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== null && value !== "";
    };

    const hasAnyFilterExcludingYear = () => hasFilter("search") || hasFilter("selectedMonths");

    return (
        <div className="flex flex-col gap-6">
            <YearlyCycleLine
                year={filters.year}
                onYearChange={handleYearChange}
                onMonthClick={handleMonthClick}
                selectedMonths={filters.selectedMonths}
            />
            <AnnualCycleTable
                filters={filters}
                onFilterChange={setFilter}
                onClearFilters={handleClearFilters}
                hasFilter={hasFilter}
                hasAnyFilter={hasAnyFilterExcludingYear}
            />
        </div>
    );
}
